<template>
  <div class="card" @click="onClickCard">
    <div class="status" :class="{ closed: isClosed }">{{ status }}</div>
    <img :src="image" alt="ogp" width="280" loading="lazy" />
    <div class="text-container">
      <div class="name">{{ title }}</div>
      <div class="department">{{ department }}</div>
      <div class="deadline">{{ $t("contestCard.deadlineLabel") }}{{ deadline }}</div>
      <div class="free">{{ free }}</div>
      <div class="tag">{{ tag }}</div>
    </div>
  </div>
</template>

<script lang="ts">
import Vue, { PropType } from "vue";
import { compareAsc, format } from "date-fns";
import { getDateFnsLocale } from "@/lib/utils/i18n/locale";
import { optimizeImage } from "@/lib/utils/url";

export default Vue.extend<Data, Methods, Computed, Props>({
  props: {
    contest: Object as PropType<Contest>,
  },
  computed: {
    id() {
      const { contest } = this;
      return contest.id;
    },
    title() {
      const { contest } = this;
      return contest.contest;
    },
    department() {
      const { contest } = this;
      return contest.department;
    },
    free() {
      const { contest } = this;
      return contest.free;
    },
    tag() {
      const { genre, category } = this;

      let tag = "";

      if (genre.length) {
        genre.forEach((x) => (tag += `#${x} `));
      }

      if (category.length) {
        category.forEach((x) => (tag += `#${x} `));
      }

      return tag;
    },
    image() {
      const { contest } = this;
      const { url } = contest.image;
      return url && optimizeImage(`${url}?fit=fill&fill=blur&w=600&h=315`);
    },
    deadline() {
      const { contest } = this;
      const { deadline } = contest;

      if (!deadline) return "-";

      const locale = getDateFnsLocale(this.$i18n.locale);
      const formatStr = this.$t("common.deadlineFormat") as string;
      return format(deadline, formatStr, { locale });
    },
    genre() {
      const { contest } = this;
      return contest.genre;
    },
    category() {
      const { contest } = this;
      return contest.category;
    },
    award() {
      const { contest } = this;
      return contest.award;
    },
    content() {
      const { contest } = this;
      return contest.content;
    },
    homepage() {
      const { contest } = this;
      return contest.homepage;
    },
    isClosed() {
      const { contest } = this;
      const currentDate = new Date();
      const contestDeadline = new Date(contest.deadline);
      return compareAsc(currentDate, contestDeadline) === 1;
    },
    status() {
      return this.isClosed ? this.$t("contestCard.status.closed") : this.$t("contestCard.status.open");
    },
  },
  methods: {
    onClickCard() {
      const { $router, id, contest } = this;
      // version は任意。2 のコンテストだけ新しい詳細画面へ遷移する
      if (contest.version === 2) {
        $router.push({ name: "eventContestV2Detail", params: { id } });
        return;
      }
      $router.push({ name: "eventContestDetail", params: { id } });
    },
  },
});

interface Props {
  contest: Contest;
}

interface Data { }

interface Computed {
  id: string;
  title: string;
  department: string;
  free: string;
  tag: string;
  image?: string;
  deadline: string;
  genre: string[];
  category: string[];
  award: string;
  content: string;
  homepage: string;
  isClosed: boolean;
  status: string;
}

interface Methods {
  onClickCard(): void;
}

type Contest = {
  [key: string]: any;
};
</script>

<style lang="scss" scoped>
.card {
  position: relative;
  box-sizing: border-box;
  border-radius: var(--radius-sm);
  box-shadow: var(--shadow-md);
  width: 280px;
  cursor: pointer;
  transition: all 0.3s ease 0s;
  background-color: var(--color-background-normal);

  &:hover {
    transform: scale(1.05);
  }

  img {
    border-radius: var(--radius-sm) var(--radius-sm) 0 0;
  }

  .status {
    position: absolute;
    top: 0px;
    left: 0px;
    box-sizing: border-box;
    padding: 2px 8px;
    border-radius: var(--radius-sm) 0 var(--radius-sm) 0;
    font-weight: var(--font-weight-bold);
    font-size: 0.9em;
    color: var(--color-text-on-fixed-bg);
    background-color: var(--color-primary);
    border: 1px solid var(--color-primary);

    &.closed {
      color: var(--color-text-low);
      background-color: var(--color-surface-muted);
      border: 1px solid var(--color-border);
    }
  }

  .text-container {
    box-sizing: border-box;
    padding: 10px;
    color: var(--color-text-normal);

    * {
      margin-bottom: 0.5em;
    }

    *:last-of-type {
      margin-bottom: 0;
    }

    .name {
      font-weight: var(--font-weight-bold);
      font-size: 1.3em;
      color: var(--color-text-normal);
    }

    .department {
      font-weight: var(--font-weight-bold);
      color: var(--color-text-low);
    }

    .deadline {
      font-weight: var(--font-weight-bold);
      color: var(--color-text-low);
    }

    .free {
      /** 省略するスタイル */
      overflow: hidden;
      text-overflow: ellipsis;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      color: var(--color-text-normal);
    }

    .tag {
      font-weight: var(--font-weight-bold);
      font-size: 0.9em;
      color: var(--color-primary);
    }
  }
}
</style>
